import { AbstractControl, ValidationErrors } from '@angular/forms';
import { FieldContext, FieldTree, PathKind, ValidationError } from '@angular/forms/signals';

export interface TranslationValidationError extends ValidationError {
  readonly translationKey: string;
}

export function translationError(kind: string, translationKey: string): TranslationValidationError {
  return { kind, translationKey };
}

/** In Angular ≥21.2.13 markAsTouched() is self-only; newer versions mark descendants too — delete this after the upgrade. */
export function markAllAsTouched(field: FieldTree<unknown>): void {
  const state = field();
  state.markAsTouched();
  const value = state.value();
  if (value !== null && typeof value === 'object') {
    for (const key of Object.keys(value)) {
      const child = (field as unknown as Record<string, unknown>)[key];
      if (typeof child === 'function') {
        markAllAsTouched(child as FieldTree<unknown>);
      }
    }
  }
}

export function resolveTranslationKey(errors: ValidationErrors | readonly ValidationError[] | null | undefined): string | null {
  if (!errors) {
    return null;
  }
  if (!Array.isArray(errors) && typeof errors['translationKey'] === 'string') {
    return errors['translationKey'];
  }
  for (const error of Object.values(errors)) {
    if (error && typeof error === 'object' && typeof (error as Record<string, unknown>)['translationKey'] === 'string') {
      return (error as TranslationValidationError).translationKey;
    }
  }
  return null;
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

function trimmed<TValue>(value: TValue): TValue {
  return typeof value === 'string' ? (value.trim() as TValue) : value;
}

/**
 * Required-field rule: fails for `null`, `undefined` and blank strings with the given translation key.
 * Unlike the format validators below it does not accept an empty value, so callers decide when it
 * applies (e.g. only when a form is validated for completeness/submit/signature etc.).
 */
export function validateRequired<TValue>(
  translationKey: string,
  kind = 'required',
): (ctx: FieldContext<TValue, PathKind.Child>) => TranslationValidationError | undefined {
  return ctx => (isBlank(ctx.value()) ? translationError(kind, translationKey) : undefined);
}

/**
 * Adapts a reactive-forms validator for signal forms.
 * Supports validators that only use `control.value`.
 */
export function validateWithControlValidator<TValue>(
  validator: (control: AbstractControl) => ValidationErrors | null,
  kind = 'custom',
): (ctx: FieldContext<TValue, PathKind.Child>) => TranslationValidationError | undefined {
  return ctx => {
    const errors = validator({ value: trimmed(ctx.value()) } as AbstractControl);
    const translationKey = errors?.['translationKey'];
    return typeof translationKey === 'string' ? translationError(kind, translationKey) : undefined;
  };
}

/**
 * Adapts a reactive-forms validator for optional signal fields.
 * Skips validation for empty values.
 */
export function validateOptionalWithControlValidator<TValue>(
  validator: (control: AbstractControl) => ValidationErrors | null,
  kind = 'custom',
): (ctx: FieldContext<TValue, PathKind.Child>) => TranslationValidationError | undefined {
  const signalValidator = validateWithControlValidator<TValue>(validator, kind);

  return ctx => {
    if (isBlank(ctx.value())) {
      return undefined;
    }

    return signalValidator(ctx);
  };
}
